import * as PIXI from 'pixi.js';
import { Config } from '../video-data/config';
import { TimeAwareMediaAsset } from './asset/TimeAwareMediaAsset';
import { BaseGroup } from './BaseGroup';
import type { BaseAssetSettings } from './asset/BaseAsset';
import type { MediaSourceData } from './asset/MediaAsset';
import { getAttributesToSerialize, NumberAttribute } from './attributes';

/**
 * The CompositingAsset allows for the compositing of two media assets, a background and a foreground into an output
 * texture. This texture can then be used as a source for a media asset through our linkedMediaAsset property.
 */
export class CompositingAsset extends BaseGroup {
    override _classType = 'CompositingAsset';

    #foreground: TimeAwareMediaAsset | null;
    #background: TimeAwareMediaAsset | null;
    #renderTexture: PIXI.RenderTexture | null;

    @NumberAttribute({ serialize: true, min: 0, max: 1 })
    public brightness = 1;

    constructor(container: any, settings: BaseAssetSettings) {
        super(container, settings);

        // We want to keep our render texture landscape for the most amount of flexibility in terms of scaling and
        // cropping. If we match the aspect ratio to the output resolution, we can't properly crop the texture to fit
        // the avatar in certain scene designs.
        const { width, height } = this.#getRenderTextureResolution();
        this.#renderTexture = PIXI.RenderTexture.create({ width, height });
    }

    #getRenderTextureResolution() {
        // we want to keep a landscape render texture for the most flexibility in terms of scaling and cropping
        let { width, height } = this.root.outputResolution;
        if (
            this.root.aspectRatio === Config.VERTICAL_ASPECT_RATIO
            || this.root.aspectRatio === Config.SQUARE_ASPECT_RATIO
        ) {
            width = height * 16 / 9;
        }
        return { width, height };
    }

    get foreground(): TimeAwareMediaAsset | null {
        return this.#foreground ?? null;
    }

    addForeground(id?: string) {
        const settings = {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            autoDraw: this.autoDraw,
            name: 'ForegroundMediaAsset',
            updateParentOnRedraw: true,
        };
        if (id) {
            settings['id'] = id;
        }
        this.#foreground = new TimeAwareMediaAsset(this, settings);
        this._addAssetAt(this.#foreground, 1);
        this.requiresRedraw = true;

        return this.#foreground;
    }

    removeForeground() {
        if (!this.foreground) {
            throw new Error('removeForeground called while CompositingAsset has no foreground');
        }
        this.foreground.destroy();
        this.#foreground = null;
    }

    _foregroundCustomLoad(value) {
        const initAutoUpdate = this.autoDraw;
        this.autoDraw = false;
        if (!value) {
            return;
        }
        this.addForeground(value.id);
        this.#foreground.load(value);
        this.autoDraw = initAutoUpdate;
    }

    _foregroundCustomSave() {
        return this.foreground?.save() ?? null;
    }

    get background(): TimeAwareMediaAsset | null {
        return this.#background ?? null;
    }

    addBackground(id?: string) {
        const settings = {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            autoDraw: this.autoDraw,
            name: 'BackgroundMediaAsset',
            updateParentOnRedraw: true,
        };
        if (id) {
            settings['id'] = id;
        }
        this.#background = new TimeAwareMediaAsset(this, settings);
        this._addAssetAt(this.#background, 0);
        this.requiresRedraw = true;

        return this.#background;
    }

    removeBackground() {
        if (!this.background) {
            throw new Error('removeBackground called while CompositingAsset has no background');
        }
        this.background.destroy();
        this.#background = null;
    }

    _backgroundCustomLoad(value) {
        const initAutoUpdate = this.autoDraw;
        this.autoDraw = false;
        if (!value) {
            return;
        }
        this.addBackground(value.id);
        this.#background.load(value);
        this.autoDraw = initAutoUpdate;
    }

    _backgroundCustomSave() {
        return this.background?.save() ?? null;
    }

    override get requiresRedraw(): boolean {
        if (!this.getAssets().every(asset => asset.isLoaded)) {
            return true;
        }
        return super.requiresRedraw;
    }

    override set requiresRedraw(value: boolean) {
        super.requiresRedraw = value;
    }

    override get readonlyProperties() {
        // this asset is part the sceneGroup composition, as such we don't require the serialization of
        // either the classType or the id
        return [];
    }

    override get propertiesToSerialize() {
        return [
            'foreground',
            'background',
            ...getAttributesToSerialize(this),
        ];
    }

    override _build() {
        // since we're not part of the scene hierarchy, we need to manually scale the container to match the
        // output resolution
        this.scale.x = this.referenceToOutputMultiplier;
        this.scale.y = this.referenceToOutputMultiplier;

        // scale and position the assets
        if (this.foreground && this.foreground.isLoaded) {
            if (this.root.aspectRatio === Config.VERTICAL_ASPECT_RATIO
            || this.root.aspectRatio === Config.SQUARE_ASPECT_RATIO
            ) {
                this.foreground.fillMode = Config.FILL_COVER;
            }
            else {
                this.foreground.fillMode = Config.FILL_CONTAIN;
            }
            // center asset
            this.foreground.x = ((this.foreground.height * 16 / 9) / 2) - (this.foreground.width / 2);
        }
        if (this.background && this.background.isLoaded && this.background.width !== 0 && this.background.height !== 0) {
            // scale asset to fill the render texture
            const multi = this.#renderTexture.width / (this.background.width * this.referenceToOutputMultiplier);
            this.background.x = 0;
            this.background.y = 0;
            this.background.scale.x = multi;
            this.background.scale.y = multi;
        }
    }

    _updateMediaTexture() {
        if (!this.root.renderer) {
            return;
        }
        if (!this.foreground && !this.background) {
            return;
        }

        this.foreground?._updateMediaTexture();
        this.background?._updateMediaTexture();

        this.root.renderer.render(this, { renderTexture: this.#renderTexture });
    }

    get _displayTexture(): PIXI.Texture {
        return this.#renderTexture;
    }

    get baseTexture(): PIXI.BaseTexture {
        return this._displayTexture.baseTexture;
    }

    /**
     * Forwarding presenter properties for linked media asset property propagation
     */
    get mediaId(): string {
        return this.foreground?.mediaId || '';
    }

    get title(): string {
        return this.foreground?.title || '';
    }

    get mediaSourceId(): string {
        return this.foreground?.mediaSourceId || '';
    }

    get license(): string {
        return this.foreground?.license || '';
    }

    get isEditorial(): boolean {
        return this.foreground?.isEditorial || false;
    }

    get mediaSource(): number {
        return this.foreground?.mediaSource || undefined;
    }

    get mediaAssetType(): typeof Config.ALL_BACKGROUND_TYPES[0] {
        return this.foreground?.mediaAssetType || null;
    }

    get sourceRenderWidth(): number {
        return this.#renderTexture.width;
    }

    get sourceRenderHeight(): number {
        return this.#renderTexture.height;
    }

    get renderUrl(): string {
        return this.foreground?.renderUrl || '';
    }

    get previewUrl(): string {
        return this.foreground?.previewUrl || '';
    }

    get posterUrl(): string | number {
        return this.foreground?.posterUrl || '';
    }

    get thumbnailUrl(): string {
        return this.foreground?.thumbnailUrl || '';
    }

    get sourceDuration(): number {
        return this.foreground?.sourceDuration || 0;
    }

    get hasAudio(): boolean {
        return this.foreground?.hasAudio || false;
    }

    get hasVoiceover(): boolean {
        return true;
    }

    get isVideo(): boolean {
        return true;
    }

    get volume(): number {
        return this.foreground?.volume || 1;
    }

    get sourceData(): MediaSourceData {
        return this.foreground?.sourceData || null;
    }

    async onSourceLoaded() {
        await this.foreground?.onSourceLoaded();
        await this.background?.onSourceLoaded();
    }

    generateNewAssetIds() {
        if (this.foreground) {
            this.foreground.generateNewId();
        }
        if (this.background) {
            this.background.generateNewId();
        }
    }

    override get failedToLoad(): boolean {
        return false;
    }

    /**
     * Duration of the asset is the duration of the foreground or the background,
     * whichever is shorter
     */
    override get duration(): number {
        let duration = 0;
        if (this.foreground) {
            duration = this.foreground.duration;
        }
        if (this.background
            && this.background.mediaAssetType === Config.VIDEO_TYPE_BACKGROUND
            && this.background.duration < duration
        ) {
            duration = this.background.duration;
        }
        return duration;
    }
}
